/**
 * Surveys advanced-filter document — a versioned value the web filter panel
 * (`SurveyFilterPanel`) edits and evaluates CLIENT-SIDE over the complete
 * survey index. One schema + ONE evaluator, both in `@releve/shared` (the
 * people/projects template on `filterDocumentPrimitives.ts`).
 *
 * The document is AD HOC (component state for the session) and has NO seeded
 * default — `EMPTY_SURVEY_FILTER_DOCUMENT` is the only starting value.
 *
 * ── v1 vocabulary ───────────────────────────────────────────────────────────
 *  - `survey.status`          string family over the status token
 *                             (`draft` / `open` / `closed`; never NULL).
 *  - `survey.project_id`      string family (NULL = no project, reachable
 *                             through `is_empty` or `include_empty` — the
 *                             panel's "No project" option).
 *  - `survey.created_by`      string family over the creator's user id (the
 *                             panel labels options from `created_by_name`;
 *                             NULL = creator gone, reachable as empty).
 *  - `survey.response_state`  string family over a DERIVED token:
 *                             `'has_responses'` when `response_count > 0`,
 *                             else `'none'` (never `''`, so no "(empty)").
 *  - `survey.last_response_on` DATE family over `last_response_on`.
 *
 * ── THE DATE IS AN ORG-ZONE CALENDAR DAY, RESOLVED BY THE MAPPER ────────────
 * `last_response_at` is a `timestamptz` instant (docs/DATES.md). The grid's
 * cell shows it as the day it falls on IN THE ORGANIZATION'S TIMEZONE
 * (`formatOrgDay`), so the filter must compare that same day or a response at
 * 00:30 Amsterdam would display as one day and filter as another. The web
 * mapper therefore stamps `last_response_on` (`yyyy-MM-dd`, org zone) on each
 * row and this evaluator compares it verbatim (`toLocalCalendarDay` returns a
 * calendar day unchanged). The evaluator stays zone-free — a server consumer
 * resolves the day with `dbCalendarDayInZone` the same way. A survey with no
 * responses (NULL) matches no range.
 *
 * Per docs/INDEX_GRID_PATTERN.md step 9b the grid runs the panel only — ZERO
 * filterable columns. Fields are `survey.<field>` over the MAPPED rows.
 *
 * `countActiveSurveyConditions` carries the entity in its name because the
 * bare `countActiveConditions` is already exported from the barrel.
 */
import { z } from "zod";
import { makeDateConditionSchema, makeFilterDocumentSchema, makeStringConditionSchema, matchesDateOp, matchesStringOp, toLocalCalendarDay, } from "./filterDocumentPrimitives.js";
// ---------------------------------------------------------------------------
// Field vocabulary (v1)
// ---------------------------------------------------------------------------
export const SURVEY_FILTER_STRING_FIELDS = [
    "survey.status",
    "survey.project_id",
    "survey.created_by",
    "survey.response_state",
];
export const SURVEY_FILTER_DATE_FIELDS = ["survey.last_response_on"];
/** The two derived `survey.response_state` tokens. */
export const SURVEY_RESPONSE_STATES = ["has_responses", "none"];
// ---------------------------------------------------------------------------
// Conditions + document
// ---------------------------------------------------------------------------
const stringConditionSchema = makeStringConditionSchema(SURVEY_FILTER_STRING_FIELDS);
const dateConditionSchema = makeDateConditionSchema(SURVEY_FILTER_DATE_FIELDS);
/** Disjoint op sets ('in'/'is_empty' vs the three date ops) — `op` alone narrows. */
export const surveyFilterConditionSchema = z.union([stringConditionSchema, dateConditionSchema]);
export const surveyFilterDocumentSchema = makeFilterDocumentSchema(surveyFilterConditionSchema);
/** The empty document — "no filters active" (what Clear resets to). */
export const EMPTY_SURVEY_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [],
};
/** The derived response-state token of a row. */
export function surveyResponseState(row) {
    return row.response_count > 0 ? "has_responses" : "none";
}
/* Exhaustive switches with NO default: a new field is a TS2366 until handled. */
function stringValue(row, field) {
    switch (field) {
        case "survey.status":
            return row.status ?? "";
        case "survey.project_id":
            return row.project_id ?? "";
        case "survey.created_by":
            return row.created_by ?? "";
        case "survey.response_state":
            return surveyResponseState(row);
    }
}
function dateValue(row, field) {
    switch (field) {
        case "survey.last_response_on":
            return toLocalCalendarDay(row.last_response_on);
    }
}
function matchesCondition(row, c) {
    switch (c.op) {
        case "in":
        case "is_empty":
            return matchesStringOp(c.op, stringValue(row, c.field), c.values, c.include_empty);
        case "date_gte":
        case "date_lte":
        case "date_between":
            return matchesDateOp(c.op, dateValue(row, c.field), c.min, c.max);
    }
}
/**
 * Apply a filter document to survey rows (AND of all conditions — v1 `match`
 * is 'all' only). An empty document returns the input array unchanged
 * (identity — callers can rely on reference equality to skip re-renders).
 */
export function applySurveyFilterDocument(rows, doc) {
    if (doc.conditions.length === 0)
        return rows;
    return rows.filter((row) => doc.conditions.every((c) => matchesCondition(row, c)));
}
/** Count of active conditions — the filter button's badge. */
export function countActiveSurveyConditions(doc) {
    return doc.conditions.length;
}
//# sourceMappingURL=surveyFilters.js.map